import { useState } from 'react'
import { Clock, Loader, X } from 'lucide-react'
import { useSubscription } from '../context/SubscriptionContext'
import { createPortalSession } from '../lib/auth-client'
import { getConversionPlan, formatUsd } from '../lib/plans-catalog'

/**
 * Countdown banner surfaced to users in a Stripe-native trial window. Hides
 * once trial is >7 days away, after trial ends, for past_due/canceled states,
 * for admins, and when manually dismissed. Shown fixed-bottom like the
 * regular SubscriptionBanner, but mutually exclusive (SubscriptionBanner
 * hides when `isInTrial` so they never stack).
 */
export default function TrialBanner() {
  const { status, plan, trialEnd, isInTrial, trialDaysRemaining, isAdmin, isLoading } = useSubscription()
  const [loadingAction, setLoadingAction] = useState(false)
  const [dismissed, setDismissed] = useState(false)

  const shouldShow =
    !isLoading &&
    !isAdmin &&
    !dismissed &&
    isInTrial &&
    trialDaysRemaining !== null &&
    trialDaysRemaining <= 7 &&
    status !== 'past_due' && // past_due users need payment update, not trial countdown
    status !== 'canceled'

  if (!shouldShow) {
    return null
  }

  const conversionTier = getConversionPlan(plan)
  const formattedDate = trialEnd
    ? new Date(trialEnd).toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })
    : null

  const daysLabel = trialDaysRemaining === 0
    ? 'today'
    : trialDaysRemaining === 1
      ? 'in 1 day'
      : `in ${trialDaysRemaining} days`

  const chargeCopy = conversionTier && formattedDate
    ? `You'll be charged ${formatUsd(conversionTier.priceCurrentUsd)} on ${formattedDate}.`
    : formattedDate
      ? `Subscription converts on ${formattedDate}.`
      : ''

  const handleManage = async () => {
    setLoadingAction(true)
    try {
      const result = await createPortalSession(window.location.href)
      window.location.href = result.url
    } catch {
      setLoadingAction(false)
    }
  }

  return (
    <div className="fixed bottom-0 left-0 right-0 z-50 bg-sky-50 border-t border-sky-200 px-4 py-3 shadow-lg">
      <div className="max-w-7xl mx-auto flex items-center justify-between gap-4">
        <div className="flex items-center gap-3 flex-1 min-w-0">
          <Clock className="w-5 h-5 text-sky-600 flex-shrink-0" />
          <p className="text-sm text-sky-900 truncate">
            <span className="font-medium">Your trial ends {daysLabel}.</span>
            {chargeCopy && <span className="ml-1 text-sky-800">{chargeCopy}</span>}
          </p>
        </div>
        <div className="flex items-center gap-2 flex-shrink-0">
          <button
            onClick={handleManage}
            disabled={loadingAction}
            className="px-4 py-2 bg-brand text-white text-sm font-medium rounded-lg hover:bg-brand-hover disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center gap-2"
          >
            {loadingAction ? (
              <>
                <Loader className="w-4 h-4 animate-spin" />
                Loading...
              </>
            ) : (
              'Manage'
            )}
          </button>
          <button
            onClick={() => setDismissed(true)}
            className="p-2 text-sky-700 hover:text-sky-900 hover:bg-sky-100 rounded-lg transition-colors"
            aria-label="Dismiss"
          >
            <X className="w-4 h-4" />
          </button>
        </div>
      </div>
    </div>
  )
}
